import { useEffect, useMemo, useState } from "react";
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { environment } from "environment/environment";

const EMPTY_SNAPSHOT = Object.freeze({
  connectedBrokerCount: 0,
  openPositionCount: 0,
  closedPositionCount: 0,
  brokers: [],
  refreshedAt: null,
});

const useBrokerPositionWebsocket = (
  accessToken,
  socketUrl = environment.stockApiDayRangeDataWSUrl,
  destination = environment.brokerPositionDestination
) => {
  const [snapshot, setSnapshot] = useState(EMPTY_SNAPSHOT);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState("");

  useEffect(() => {
    if (typeof window === "undefined" || !accessToken) {
      setIsConnected(false);
      return undefined;
    }

    const stompClient = new Client({
      webSocketFactory: () => new SockJS(socketUrl),
      connectHeaders: { Authorization: `Bearer ${accessToken}` },
      reconnectDelay: 5000,
      connectionTimeout: 10000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      discardWebsocketOnCommFailure: true,
      debug: () => {},
    });

    stompClient.onConnect = () => {
      setIsConnected(true);
      setConnectionError("");
      stompClient.subscribe(destination, (message) => {
        try {
          const nextSnapshot = JSON.parse(message.body);
          setSnapshot({
            ...EMPTY_SNAPSHOT,
            ...nextSnapshot,
            brokers: Array.isArray(nextSnapshot?.brokers)
              ? nextSnapshot.brokers
              : [],
          });
        } catch (error) {
          console.error("Unable to read broker position update:", error);
          setConnectionError("The latest broker position update could not be read.");
        }
      });
    };

    stompClient.onStompError = (frame) => {
      setIsConnected(false);
      setConnectionError(
        frame.headers?.message || "Broker position connection was rejected."
      );
    };
    stompClient.onWebSocketError = () => {
      setIsConnected(false);
      setConnectionError("Unable to connect to live broker positions.");
    };
    stompClient.onWebSocketClose = () => setIsConnected(false);
    stompClient.onDisconnect = () => setIsConnected(false);
    stompClient.activate();

    return () => {
      setIsConnected(false);
      if (stompClient.active) {
        void stompClient.deactivate().catch((error) => {
          console.error("Unable to close broker position connection:", error);
        });
      }
    };
  }, [accessToken, destination, socketUrl]);

  const positions = useMemo(
    () => snapshot.brokers.flatMap((broker) =>
      (Array.isArray(broker.positions) ? broker.positions : []).map((position) => ({
        ...position,
        brokerAccountId: position.brokerAccountId || broker.brokerAccountId,
        brokerAccountName:
          position.brokerAccountName || broker.brokerAccountName,
        brokerType: position.brokerType || broker.brokerType,
      }))
    ),
    [snapshot]
  );

  return { snapshot, positions, isConnected, connectionError };
};

export default useBrokerPositionWebsocket;
